import {
  emailCompositionDefaults,
  type EmailCompositionValue,
} from '@kula-ai/components';
import {
  asList,
  toIdString,
  toAddressList,
  toSelectOptions as toOptions,
  resolveEmailUser,
  isSameEmailValue,
  type EmailUser,
} from '@/components/shared/email/emailValue';

export type SurveyEmailUser = EmailUser;

/** The subset of the survey form Formik holds for the email step. */
export interface SurveyEmailValues {
  from?: string | number;
  fromUserObject?: SurveyEmailUser;
  cc?: Array<string | number>;
  bcc?: Array<string | number>;
  toAddress?: Array<string | number>;
  subject?: string;
  body?: string;
}

/** `useEmailField` used `valueKey: 'id'`, so the form holds user ids. */
export const toSelectOptions = (users: SurveyEmailUser[] = []) =>
  toOptions(users, 'id');

export { isSameEmailValue };

/** Seeds the composer from the survey form's stored shape. */
export const toEmailValue = (
  values: SurveyEmailValues | undefined | null
): EmailCompositionValue => ({
  ...emailCompositionDefaults,
  // Shared mailboxes save their own id on `from`, so prefer the resolved object.
  from: toIdString(values?.fromUserObject?.id ?? values?.from),
  to: toAddressList(values?.toAddress),
  cc: toAddressList(values?.cc),
  bcc: toAddressList(values?.bcc),
  subject: values?.subject ?? '',
  content: values?.body ?? '',
  template: null,
});

/**
 * Maps a snapshot back to the names `useSurveyFormSubmit` reads.
 *
 * `fromUserObject` carries `isSharedEmail`, which is what turns the sender into
 * `ats_shared_email_id` rather than `sender_id`.
 */
export const fromEmailValue = (
  value: EmailCompositionValue,
  resolveUser: (_id: string) => SurveyEmailUser | undefined
): SurveyEmailValues => {
  const fromUserObject = resolveUser(value.from);

  return {
    from: fromUserObject?.id ?? value.from,
    ...(fromUserObject ? { fromUserObject } : {}),
    toAddress: asList(value.to),
    cc: value.cc ?? [],
    bcc: value.bcc ?? [],
    subject: value.subject,
    body: value.content,
  };
};

export { resolveEmailUser };
